import React, { useState, useEffect } from "react";
// GOOGLE CALENDAR DISABLED
// import {
//   createCalendarEvent,
//   isSignedIn,
//   getEventsForDateRange,
//   deleteCalendarEvent,
//   getEventsForUserBySeason,
// } from "../../services/googleCalendar";
import { useToast } from "../../context/ToastContext";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import "./CalendarScheduler.css";
import { handleGenericError } from "../../utils/errorHandler";

function CalendarScheduler({ isOpen, onClose, recruit, existingMeeting }) {
  const { showSuccess } = useToast();
  const { getVerifiedJWT } = useAuth();
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [duration, setDuration] = useState(30);
  const [authenticated, setAuthenticated] = useState(false);
  const [managersList, setManagersList] = useState([]);
  const [selectedManagers, setSelectedManagers] = useState([]);
  // const [currentUserSeasonId, setCurrentUserSeasonId] = useState(null);
  const [managerCalendarWarning, setManagerCalendarWarning] = useState(null);
  const [managerSearchQuery, setManagerSearchQuery] = useState("");
  const [showManagerSuggestions, setShowManagerSuggestions] = useState(false);

  // Helper function to flatten manager hierarchy
  const flattenManagerHierarchy = (node, result = []) => {
    if (!node) return result;

    // Add current node to result
    result.push({
      rep_id: node.rep_id,
      user_season_id: node.user_season_id,
      name: `${node.first_name} ${node.last_name}`,
      email: node.email,
      is_manager: node.is_manager,
    });

    // Recursively add all reps (direct reports)
    if (node.reps && Array.isArray(node.reps)) {
      node.reps.forEach(rep => {
        flattenManagerHierarchy(rep, result);
      });
    }

    return result;
  };

  // Fetch managers when modal opens
  useEffect(() => {
    const fetchManagers = async () => {
      if (!isOpen) return;

      try {
        const jwt = await getVerifiedJWT();
        if (!jwt) return;

        const response = await fetch(
          getApiEndpoint("/v1/company-downline/reps-by-manager"),
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${jwt}`,
            },
          }
        );

        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data.reps) {
            // Store current user's season ID
            // setCurrentUserSeasonId(result.data.reps.user_season_id);

            // Flatten the hierarchical structure
            const flatManagers = flattenManagerHierarchy(result.data.reps);
            // Filter to only include managers
            const managersOnly = flatManagers.filter(m => m.is_manager);
            setManagersList(managersOnly);

            // Set the logged-in user (root of hierarchy) as default selection
            if (result.data.reps && result.data.reps.is_manager) {
              setSelectedManagers([result.data.reps.user_season_id]);
            } else if (managersOnly.length > 0) {
              // Fallback to first manager if current user is not a manager
              setSelectedManagers([managersOnly[0].user_season_id]);
            }
          }
        }
      } catch (error) {}
    };

    fetchManagers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, getVerifiedJWT]);

  // GOOGLE CALENDAR DISABLED - Always show as not authenticated
  useEffect(() => {
    setAuthenticated(false); // isSignedIn() disabled
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Reset modal state when it closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedDate(null);
      setSelectedTime(null);
      setAvailableSlots([]);
      setManagerSearchQuery("");
      setShowManagerSuggestions(false);
      setManagerCalendarWarning(null);
      setCurrentMonth(new Date());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Regenerate time slots when duration or selected manager changes
  useEffect(() => {
    if (selectedDate) {
      // Regenerate slots with new duration or manager
      const regenerateSlots = async () => {
        setSelectedTime(null); // Clear selected time
        setAvailableSlots([]); // Clear slots while loading
        setManagerCalendarWarning(null); // Clear any previous warnings
        try {
          const slots = await generateTimeSlots(selectedDate);
          setAvailableSlots(slots);
        } catch (error) {}
      };
      regenerateSlots();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duration, selectedManagers]);

  // Get calendar data for current month
  const getDaysInMonth = date => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    return { daysInMonth, startingDayOfWeek, year, month };
  };

  // Check if a time slot conflicts with any existing events
  const hasConflict = (slotStart, slotEnd, events) => {
    return events.some(event => {
      const eventStart = new Date(event.start.dateTime || event.start.date);
      const eventEnd = new Date(event.end.dateTime || event.end.date);

      // Check if the slot overlaps with the event
      return slotStart < eventEnd && slotEnd > eventStart;
    });
  };

  // Generate available time slots for a given date
  const generateTimeSlots = async date => {
    const slots = [];
    const startHour = 9; // 9 AM
    const endHour = 17; // 5 PM
    const interval = 30; // 30 minutes

    // Fetch calendar events for the selected day
    let existingEvents = [];

    try {
      const jwt = await getVerifiedJWT();
      const dayStart = new Date(date);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(date);
      dayEnd.setHours(23, 59, 59, 999);

      // GOOGLE CALENDAR DISABLED - Only fetch local meetings
      // if (authenticated) {
      //   // Fetch from Google Calendar
      //   try {
      //     // Always fetch current user's calendar events
      //     const currentUserEvents = await getEventsForDateRange(
      //       dayStart,
      //       dayEnd,
      //       jwt
      //     );

      //     existingEvents = [...currentUserEvents];

      //     // Fetch calendar events for all selected managers
      //     const managersWithoutCalendar = [];

      //     for (const managerId of selectedManagers) {
      //       // Skip current user since we already fetched their events
      //       if (managerId === currentUserSeasonId) {
      //         continue;
      //       }

      //       const managerData = await getEventsForUserBySeason(
      //         managerId,
      //         dayStart,
      //         dayEnd,
      //         jwt
      //       );

      //       if (managerData === null) {
      //         // Manager hasn't connected their calendar
      //         const managerDetails = managersList.find(
      //           m => m.user_season_id === managerId
      //         );
      //         managersWithoutCalendar.push(
      //           managerDetails?.name || "Unknown Manager"
      //         );
      //       } else {
      //         // Manager's calendar is connected
      //         const managerEvents = managerData.events || [];

      //         // Store the manager's Google email
      //         if (managerData.google_email) {
      //           setManagerGoogleEmails(prev => ({
      //             ...prev,
      //             [managerId]: managerData.google_email,
      //           }));
      //         }

      //         // Merge events
      //         existingEvents = [...existingEvents, ...managerEvents];
      //       }
      //     }

      //     // Set warning message if any managers don't have calendars connected
      //     if (managersWithoutCalendar.length > 0) {
      //       const warningMsg = `${managersWithoutCalendar.join(", ")}'s Google Calendar${managersWithoutCalendar.length > 1 ? "s are" : " is"} not connected. Showing availability based on other calendars only.`;
      //       setManagerCalendarWarning(warningMsg);
      //     } else {
      //       setManagerCalendarWarning(null);
      //     }
      //   } catch (error) {
      //     // Continue without filtering if we can't fetch events
      //   }
      // } else {

      // Google Calendar not connected - fetch local meetings to prevent double-booking
      try {
        const response = await fetch(
          getApiEndpoint("/v1/recruiting/upcoming-meetings"),
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${jwt}`,
            },
          }
        );

        if (response.ok) {
          const result = await response.json();
          const localMeetings = result.data || [];

          // Filter meetings for the selected day and convert to event format
          const dayMeetings = localMeetings
            .filter(meeting => {
              const meetingDate = new Date(meeting.time);
              return meetingDate >= dayStart && meetingDate <= dayEnd;
            })
            .map(meeting => ({
              start: { dateTime: new Date(meeting.time).toISOString() },
              end: {
                dateTime: new Date(
                  new Date(meeting.time).getTime() + 30 * 60 * 1000
                ).toISOString(),
              },
            }));

          existingEvents = [...existingEvents, ...dayMeetings];
        }
      } catch (error) {
        // Local fetch failed, continue without filtering
      }

      // }
    } catch (error) {
      // Continue without filtering if we can't fetch events
    }

    for (let hour = startHour; hour < endHour; hour++) {
      for (let minute = 0; minute < 60; minute += interval) {
        const timeDate = new Date(date);
        timeDate.setHours(hour, minute, 0, 0);

        // Allow past times for scheduling meetings in the past
        const slotEnd = new Date(timeDate);
        slotEnd.setMinutes(slotEnd.getMinutes() + duration);

        // Check if this slot conflicts with any existing events (from both calendars)
        const conflicted = hasConflict(timeDate, slotEnd, existingEvents);

        if (!conflicted) {
          const timeString = timeDate.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          });
          slots.push({
            time: timeString,
            dateTime: timeDate,
          });
        }
      }
    }

    return slots;
  };

  // Handle date selection
  const handleDateSelect = async day => {
    const selected = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      day
    );
    setSelectedDate(selected);
    setSelectedTime(null);
    setAvailableSlots([]); // Clear slots while loading

    // Generate available time slots for selected date (filtering out conflicts)
    try {
      const slots = await generateTimeSlots(selected);
      setAvailableSlots(slots);
    } catch (error) {
      handleGenericError(error, "Failed to load available time slots");
    }
  };

  // Handle time slot selection
  const handleTimeSelect = slot => {
    setSelectedTime(slot);
  };

  // Navigate to previous month
  const previousMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1)
    );
    setSelectedDate(null);
    setSelectedTime(null);
  };

  // Navigate to next month
  const nextMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1)
    );
    setSelectedDate(null);
    setSelectedTime(null);
  };

  // Schedule the meeting
  const handleScheduleMeeting = async () => {
    if (!selectedTime) return;

    setLoading(true);

    try {
      const jwt = await getVerifiedJWT();

      // GOOGLE CALENDAR DISABLED - Always save to local database only
      // if (authenticated) {
      //   // If this is a reschedule (existing event), delete the old event first
      //   if (existingEventId) {
      //     try {
      //       await deleteCalendarEvent(existingEventId, jwt);
      //     } catch (deleteErr) {
      //       // Continue with creating new event even if delete fails
      //     }
      //   }

      //   const endTime = new Date(selectedTime.dateTime);
      //   endTime.setMinutes(endTime.getMinutes() + duration);

      //   // Build comma-separated email list and manager info for all selected managers (excluding current user)
      //   const otherManagers = selectedManagers.filter(
      //     id => id !== currentUserSeasonId
      //   );

      //   const attendeesEmails = [];
      //   const managerNames = [];
      //   const managerIds = [];

      //   for (const managerId of otherManagers) {
      //     const managerDetails = managersList.find(
      //       m => m.user_season_id === managerId
      //     );
      //     if (managerDetails) {
      //       // Prefer Google email if available
      //       const googleEmail = managerGoogleEmails[managerId];
      //       const email = googleEmail || managerDetails.email;

      //       if (email) {
      //         attendeesEmails.push(email);
      //       }

      //       managerNames.push(managerDetails.name);
      //       managerIds.push(managerId.toString());
      //     }
      //   }

      //   const attendeesEmail = attendeesEmails.join(",");

      //   const eventData = {
      //     summary: `Meeting with ${recruit ? recruit.name : "Recruit"}`,
      //     description:
      //       managerNames.length > 0
      //         ? `Recruiting meeting with ${managerNames.join(", ")}`
      //         : "Recruiting meeting",
      //     startDateTime: selectedTime.dateTime.toISOString(),
      //     endDateTime: endTime.toISOString(),
      //     attendees: attendeesEmail,
      //     recruitId: recruit?.id,
      //     managerIds: managerIds.join(","),
      //     managerNames: managerNames.join(","),
      //   };

      //   // Create event on current user's calendar with manager as attendee
      //   const result = await createCalendarEvent(eventData, jwt);

      //   if (result.success) {
      //     const successMessage = existingEventId
      //       ? "Meeting rescheduled successfully!"
      //       : otherManagers.length > 0
      //         ? `Meeting scheduled and invitation${otherManagers.length > 1 ? "s" : ""} sent to ${otherManagers.length} manager${otherManagers.length > 1 ? "s" : ""}!`
      //         : "Meeting scheduled successfully!";
      //     showSuccess(successMessage);
      //     // Pass the meeting info back to parent component
      //     if (onClose) {
      //       onClose({
      //         scheduled: true,
      //         meetingTime: selectedTime.dateTime,
      //         duration: duration,
      //         recruitId: recruit?.id,
      //         eventId: result.event.id,
      //       });
      //     }
      //   }
      // } else {
      {
        // Google Calendar not connected - save to local database only

        // If this is a reschedule (existingMeeting provided), delete the old meeting first
        if (existingMeeting && existingMeeting.time) {
          try {
            const deleteResponse = await fetch(
              getApiEndpoint(
                `/v1/recruiting/leads/${recruit.id}/delete-meeting`
              ),
              {
                method: "DELETE",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${jwt}`,
                },
                body: JSON.stringify({
                  meeting_date: existingMeeting.time,
                }),
              }
            );

            if (deleteResponse.ok) {
              await deleteResponse.json();
            }
          } catch (deleteErr) {
            // Continue with scheduling even if delete fails
          }
        }

        // Schedule the new meeting
        const response = await fetch(
          getApiEndpoint(`/v1/recruiting/leads/${recruit.id}/schedule-meeting`),
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${jwt}`,
            },
            body: JSON.stringify({
              meeting_date: selectedTime.dateTime.toISOString(),
              notes: `Meeting ${existingMeeting ? "rescheduled" : "scheduled"} for ${selectedTime.time} (${duration} min)`,
            }),
          }
        );

        if (response.ok) {
          const successMessage = existingMeeting
            ? "Meeting rescheduled successfully!"
            : "Meeting scheduled successfully!";
          showSuccess(successMessage);
          // Pass the meeting info back to parent component
          if (onClose) {
            onClose({
              scheduled: true,
              meetingTime: selectedTime.dateTime,
              duration: duration,
              recruitId: recruit?.id,
              eventId: null, // No Google Calendar event
            });
          }
        } else {
          throw new Error("Failed to save meeting");
        }
      }
      // }
    } catch (err) {
      handleGenericError(err, "Failed to schedule meeting. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Check if date is available (allow past and future dates within reasonable range)
  const isDateAvailable = day => {
    const date = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      day
    );
    // Allow dates within the past year and future 60 days
    const minDate = new Date();
    minDate.setFullYear(minDate.getFullYear() - 1);
    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + 60);

    return date >= minDate && date <= maxDate;
  };

  // Check if date is today
  const isToday = day => {
    const date = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      day
    );
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  // Check if date is selected
  const isSelected = day => {
    if (!selectedDate) return false;
    return (
      selectedDate.getDate() === day &&
      selectedDate.getMonth() === currentMonth.getMonth() &&
      selectedDate.getFullYear() === currentMonth.getFullYear()
    );
  };

  if (!isOpen) return null;

  const { daysInMonth, startingDayOfWeek } = getDaysInMonth(currentMonth);
  const monthName = currentMonth.toLocaleDateString("en-US", { month: "long" });
  const year = currentMonth.getFullYear();
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return (
    <div className="calendar-scheduler-modal-overlay" onClick={onClose}>
      <div
        className="calendar-scheduler-modal-content"
        onClick={e => e.stopPropagation()}
      >
        <button className="calendar-scheduler-close-btn" onClick={onClose}>
          ×
        </button>

        <div className="calendar-scheduler-header">
          <div className="calendar-scheduler-header-grid">
            <div className="calendar-scheduler-header-left">
              <h2>Schedule Meeting</h2>
              {recruit && (
                <p className="calendar-scheduler-subtitle">
                  with {recruit.name}
                </p>
              )}

              {/* Calendar Status Badge */}
              <div className="calendar-scheduler-calendar-status-row">
                {authenticated ? (
                  <div className="calendar-scheduler-calendar-badge calendar-scheduler-calendar-badge-connected">
                    <span className="material-icons">check_circle</span>
                    <span>Calendar Connected</span>
                  </div>
                ) : (
                  <div className="calendar-scheduler-calendar-badge calendar-scheduler-calendar-badge-disconnected">
                    <span className="material-icons">warning</span>
                    <span>Calendar Not Connected</span>
                  </div>
                )}
              </div>

              {/* Info message for disconnected calendar */}
              {!authenticated && (
                <div className="calendar-scheduler-calendar-warning">
                  Meetings will be saved locally. Connect Google Calendar from
                  your <strong>Profile page</strong> for calendar sync &
                  reminders.
                </div>
              )}
            </div>

            <div className="calendar-scheduler-header-right">
              {/* Manager Selector */}
              {managersList.length > 0 && (
                <div className="calendar-scheduler-manager-chips-container">
                  <label>Schedule with Managers:</label>
                  <div className="calendar-scheduler-manager-chips-wrapper">
                    {/* Display selected manager chips */}
                    {selectedManagers.length > 0 && (
                      <div className="calendar-scheduler-manager-chips">
                        {selectedManagers.map(managerId => {
                          const manager = managersList.find(
                            m => m.user_season_id === managerId
                          );
                          if (!manager) return null;

                          return (
                            <div
                              key={managerId}
                              className="calendar-scheduler-manager-chip"
                            >
                              <span className="calendar-scheduler-manager-chip-name">
                                {manager.name}
                              </span>
                              {selectedManagers.length > 1 && (
                                <button
                                  className="calendar-scheduler-manager-chip-remove"
                                  onClick={() => {
                                    setSelectedManagers(prev =>
                                      prev.filter(id => id !== managerId)
                                    );
                                  }}
                                  title="Remove manager"
                                >
                                  ×
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Searchable input field */}
                    <div className="calendar-scheduler-manager-search-wrapper">
                      <input
                        type="text"
                        className="calendar-scheduler-manager-search-input"
                        placeholder="Search managers to add..."
                        value={managerSearchQuery}
                        onChange={e => setManagerSearchQuery(e.target.value)}
                        onFocus={() => setShowManagerSuggestions(true)}
                        onBlur={() => {
                          // Delay hiding to allow click on suggestion
                          setTimeout(
                            () => setShowManagerSuggestions(false),
                            200
                          );
                        }}
                      />

                      {/* Suggestions dropdown */}
                      {showManagerSuggestions && managerSearchQuery && (
                        <div className="calendar-scheduler-manager-suggestions">
                          {managersList
                            .filter(
                              m =>
                                !selectedManagers.includes(m.user_season_id) &&
                                m.name
                                  .toLowerCase()
                                  .includes(managerSearchQuery.toLowerCase())
                            )
                            .map(manager => (
                              <div
                                key={manager.user_season_id}
                                className="calendar-scheduler-manager-suggestion-item"
                                onClick={() => {
                                  setSelectedManagers(prev => [
                                    ...prev,
                                    manager.user_season_id,
                                  ]);
                                  setManagerSearchQuery("");
                                  setShowManagerSuggestions(false);
                                }}
                              >
                                {manager.name}
                              </div>
                            ))}
                          {managersList.filter(
                            m =>
                              !selectedManagers.includes(m.user_season_id) &&
                              m.name
                                .toLowerCase()
                                .includes(managerSearchQuery.toLowerCase())
                          ).length === 0 && (
                            <div className="calendar-scheduler-manager-suggestion-empty">
                              {selectedManagers.length === managersList.length
                                ? "All managers selected"
                                : "No managers found"}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="calendar-scheduler-body">
          {/* Calendar Section */}
          <div className="calendar-scheduler-calendar-section">
            <div className="calendar-header">
              <button onClick={previousMonth} className="calendar-nav-btn">
                ‹
              </button>
              <h3>
                {monthName} {year}
              </h3>
              <button onClick={nextMonth} className="calendar-nav-btn">
                ›
              </button>
            </div>

            <div className="calendar-grid">
              <div className="calendar-weekdays">
                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(day => (
                  <div key={day} className="calendar-weekday">
                    {day}
                  </div>
                ))}
              </div>

              <div className="calendar-days">
                {[...Array(startingDayOfWeek)].map((_, index) => (
                  <div key={`empty-${index}`} className="calendar-day empty" />
                ))}

                {[...Array(daysInMonth)].map((_, index) => {
                  const day = index + 1;
                  const available = isDateAvailable(day);
                  const today = isToday(day);
                  const selected = isSelected(day);

                  return (
                    <div
                      key={day}
                      className={`calendar-day ${available ? "available" : "unavailable"} ${today ? "today" : ""} ${selected ? "selected" : ""}`}
                      onClick={() => available && handleDateSelect(day)}
                    >
                      {day}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="duration-selector">
              <label>Duration:</label>
              <select
                value={duration}
                onChange={e => setDuration(Number(e.target.value))}
              >
                <option value={15}>15 min</option>
                <option value={30}>30 min</option>
                <option value={45}>45 min</option>
                <option value={60}>60 min</option>
              </select>
            </div>
          </div>

          {/* Time Slots Section */}
          <div className="calendar-scheduler-timeslots-section">
            <div className="timeslots-header">
              <h3>
                {selectedDate
                  ? selectedDate.toLocaleDateString("en-US", {
                      weekday: "long",
                      month: "long",
                      day: "numeric",
                    })
                  : "Select a date"}
              </h3>
              <p className="timezone-info">{userTimezone}</p>
            </div>

            {/* Manager Calendar Warning */}
            {managerCalendarWarning && (
              <div
                style={{
                  padding: "12px",
                  backgroundColor: "#fff3cd",
                  border: "1px solid #ffc107",
                  borderRadius: "6px",
                  marginBottom: "16px",
                }}
              >
                <p
                  style={{
                    margin: 0,
                    color: "#856404",
                    fontSize: "0.85rem",
                  }}
                >
                  ℹ️ {managerCalendarWarning}
                </p>
              </div>
            )}

            <div className="timeslots-list">
              {!selectedDate && (
                <div className="timeslots-empty">
                  <p>Please select a date to view available times</p>
                </div>
              )}

              {selectedDate && availableSlots.length === 0 && (
                <div className="timeslots-empty">
                  <p>No available times for this date</p>
                </div>
              )}

              {selectedDate &&
                availableSlots.map((slot, index) => (
                  <button
                    key={index}
                    className={`timeslot-btn ${selectedTime?.time === slot.time ? "selected" : ""}`}
                    onClick={() => handleTimeSelect(slot)}
                  >
                    {slot.time}
                  </button>
                ))}
            </div>

            {selectedTime && (
              <div className="calendar-scheduler-footer">
                <button
                  className="schedule-btn"
                  onClick={handleScheduleMeeting}
                  disabled={loading || !selectedTime}
                >
                  {loading
                    ? "Scheduling..."
                    : !authenticated
                      ? "Schedule (Local Only)"
                      : "Confirm"}
                </button>
              </div>
            )}

            {/* Debug info */}
            {selectedDate && selectedTime && (
              <div
                style={{ marginTop: "8px", fontSize: "12px", color: "#666" }}
              >
                Selected: {selectedDate.toLocaleDateString()} at{" "}
                {selectedTime.time}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CalendarScheduler;
